import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * AR Payment Applications RPC Contract
 *
 * Provides endpoints for listing payment applications with filtering by paymentId or invoiceId.
 * Enables cross-referencing between payments and invoices.
 */

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

/**
 * Pagination schema
 */
export const ArPaymentApplicationPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * List AR payment applications input schema
 */
export const ListArPaymentApplicationsInputSchema = z.object({
  paymentId: UUIDSchema.optional(),
  invoiceId: UUIDSchema.optional(),
  applicationDateFrom: z.string().optional(),
  applicationDateTo: z.string().optional(),
  sortBy: z.enum(["applicationDate", "appliedAmount"]).optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * Payment summary inside payment application list item
 */
export const ArPaymentApplicationPaymentSummarySchema = z.object({
  id: UUIDSchema,
  referenceNumber: z.string().nullable(),
  paymentDate: z.string().nullable(),
  paymentAmount: DecimalStringSchema.nullable(),
  payerName: z.string().nullable(),
});

/**
 * Invoice summary inside payment application list item
 */
export const ArPaymentApplicationInvoiceSummarySchema = z.object({
  id: UUIDSchema,
  invoiceNumber: z.string().nullable(),
  invoiceDate: z.string(),
  netAmount: DecimalStringSchema.nullable(),
  balanceDue: DecimalStringSchema.nullable(),
  status: z.string(),
});

/**
 * AR payment application list item schema
 */
export const ArPaymentApplicationListItemSchema = z.object({
  id: UUIDSchema,
  paymentId: UUIDSchema,
  invoiceId: UUIDSchema,
  appliedAmount: DecimalStringSchema.nullable(),
  applicationDate: z.string().nullable(),
  sequenceNumber: z.number().nullable(),
  payment: ArPaymentApplicationPaymentSummarySchema.nullable(),
  invoice: ArPaymentApplicationInvoiceSummarySchema.nullable(),
});

/**
 * List AR payment applications response schema
 */
export const ListArPaymentApplicationsResponseSchema = z.object({
  paymentApplications: z.array(ArPaymentApplicationListItemSchema),
  pagination: ArPaymentApplicationPaginationSchema,
});

/**
 * Create (manually allocate a payment to an invoice).
 */
/** Decimal string that must represent a strictly-positive amount. */
const PositiveDecimalStringSchema = DecimalStringSchema.refine(
  (v) => Number(v) > 0,
  { message: "Applied amount must be greater than 0" },
);

export const CreateArPaymentApplicationInputSchema = z.object({
  paymentId: UUIDSchema,
  invoiceId: UUIDSchema,
  appliedAmount: PositiveDecimalStringSchema,
  applicationDate: z.string().nullable().optional(),
});

export const CreateArPaymentApplicationResponseSchema = z.object({
  id: UUIDSchema,
  paymentId: UUIDSchema,
  invoiceId: UUIDSchema,
});

/**
 * Delete (remove a manually-created allocation).
 */
export const DeleteArPaymentApplicationInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteArPaymentApplicationResponseSchema = z.object({
  success: z.literal(true),
  invoiceId: UUIDSchema,
});

/**
 * AR Payment Applications RPC Contract
 */
export const arPaymentApplicationsContracts = {
  list: oc
    .input(ListArPaymentApplicationsInputSchema)
    .output(ListArPaymentApplicationsResponseSchema)
    .route({ method: "GET" }),
  create: oc
    .input(CreateArPaymentApplicationInputSchema)
    .output(CreateArPaymentApplicationResponseSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeleteArPaymentApplicationInputSchema)
    .output(DeleteArPaymentApplicationResponseSchema)
    .route({ method: "POST" }),
} as const;

// Type exports
export type ArPaymentApplicationPagination = z.infer<
  typeof ArPaymentApplicationPaginationSchema
>;
export type ListArPaymentApplicationsInput = z.infer<
  typeof ListArPaymentApplicationsInputSchema
>;
export type ListArPaymentApplicationsResponse = z.infer<
  typeof ListArPaymentApplicationsResponseSchema
>;
export type ArPaymentApplicationListItem = z.infer<
  typeof ArPaymentApplicationListItemSchema
>;
export type ArPaymentApplicationPaymentSummary = z.infer<
  typeof ArPaymentApplicationPaymentSummarySchema
>;
export type ArPaymentApplicationInvoiceSummary = z.infer<
  typeof ArPaymentApplicationInvoiceSummarySchema
>;
export type CreateArPaymentApplicationInput = z.infer<
  typeof CreateArPaymentApplicationInputSchema
>;
export type CreateArPaymentApplicationResponse = z.infer<
  typeof CreateArPaymentApplicationResponseSchema
>;
export type DeleteArPaymentApplicationInput = z.infer<
  typeof DeleteArPaymentApplicationInputSchema
>;
export type DeleteArPaymentApplicationResponse = z.infer<
  typeof DeleteArPaymentApplicationResponseSchema
>;
